import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DescriptionIcon from '@material-ui/icons/Description'
import HistoryIcon from '@material-ui/icons/History'
import LinkIcon from '@material-ui/icons/Link'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Link as RouterLink } from 'react-router-dom'

const WORKFLOWS = {
  timer: {
    label: 'Irrigation Timer',
    apiBase: '/api/mobileapp/irrigation/timer-voice',
    supportsUrl: true,
    defaultEntityType: 'controller',
    transcriptPlaceholder: 'Station one runs Monday Wednesday Friday for 25 minutes at 8 AM.',
    examples: [
      {
        label: 'Simple Timer',
        transcript: 'Station one runs Monday Wednesday and Friday for 25 minutes at 8 AM.'
      },
      {
        label: 'Shared Times',
        transcript: 'Station number one runs for 25 minutes. Station number two runs for 15 minutes. They both start at 8 AM and 4 PM every day of the week.'
      }
    ]
  },
  stationOhms: {
    label: 'Station Ohms',
    apiBase: '/api/mobileapp/irrigation/station-ohms-voice',
    supportsUrl: false,
    defaultEntityType: 'controller',
    transcriptPlaceholder: 'Station 1 reads 27 ohms. Station 2 reads 14. Or: 42, 23, 24, 42.',
    examples: [
      {
        label: 'Explicit Ohms',
        transcript: 'Station one reads 27 ohms. Station two reads 14. Station three reads 42.'
      },
      {
        label: 'Sequence Ohms',
        transcript: '42, 23, 24, 42'
      }
    ]
  },
  arbor: {
    label: 'Arbor Scope',
    apiBase: '/api/mobileapp/arbor/voice',
    supportsUrl: false,
    defaultEntityType: 'arbor_scope',
    transcriptPlaceholder: 'Tree one is a mesquite, 25 feet tall, 14 inch DBH, 20 foot canopy, medium density, raise canopy and remove deadwood.',
    examples: [
      {
        label: 'Single Trim',
        transcript: 'Tree one is a mesquite in the front yard. It is about 35 feet tall. Girth is 8 feet around. Canopy starts around 13 feet and spreads about 45 feet. Canopy is heavy. Customer wants canopy raised over the driveway and deadwood removed. There are service lines on the east side and a clear drop zone toward the street. Haul all debris.'
      },
      {
        label: 'Group Palms',
        transcript: 'Group back wall palms. Future plan remove these next year. Internal note customer wants a budget option. One-off reason storm cleanup only. Tree one is a queen palm 30 feet tall 12 inch DBH 10 foot canopy seed pods. Tree two is a date palm 25 feet tall 14 inch DBH 12 foot canopy.'
      },
      {
        label: 'Limited Access',
        transcript: 'Tree one is a ficus in the backyard by the pool, 25 feet tall, 18 inch DBH, 30 foot canopy, limited drop zone, no lift access, rigging needed, leave logs and chip brush.'
      },
      {
        label: 'Shared Objective',
        transcript: 'For this group raise canopy and remove deadwood. Group front yard mesquites. Tree one is a mesquite 25 feet tall 12 inch DBH 20 foot canopy. Tree two is a mesquite 22 feet tall 10 inch DBH 18 foot canopy. Tree three is a ficus 18 feet tall 9 inch DBH 15 foot canopy remove the tree.'
      },
      {
        label: 'Quantity Group',
        transcript: 'Three queen palms along the back wall all about 20 feet. Seed pods and haul all debris. Clear drop zone toward the alley.'
      },
      {
        label: 'Reset Scope',
        transcript: 'For this group raise canopy and remove deadwood. Tree one is a mesquite 25 feet tall 12 inch DBH 20 foot canopy. Next tree only remove the tree. Tree two is a ficus 18 feet tall 9 inch DBH 15 foot canopy. Back to normal trim. Tree three is an olive 16 feet tall 8 inch DBH 14 foot canopy.'
      }
    ]
  }
}
const TABS = ['Summary', 'Tree Cards', 'Structured JSON', 'Transcript', 'Warnings', 'Files', 'Raw']
const FEEDBACK_CATEGORIES = [
  ['species', 'Species'],
  ['measurements', 'Size'],
  ['objectives', 'Objective'],
  ['risks', 'Risk'],
  ['access', 'Access'],
  ['disposal', 'Disposal'],
  ['notes', 'Notes']
]

const emptyFeedback = () => ({
  accepted: false,
  missed: {},
  wrong: {},
  reviewerNote: '',
  correctedFields: null
})

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1600,
    margin: '0 auto',
    backgroundColor: '#f3f5f7',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  panel: {
    padding: theme.spacing(2),
    height: '100%'
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  controlsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: theme.spacing(1)
  },
  actions: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  historyList: {
    display: 'grid',
    gap: theme.spacing(1),
    maxHeight: 680,
    overflow: 'auto',
    paddingRight: theme.spacing(0.5)
  },
  historyItem: {
    width: '100%',
    textAlign: 'left',
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(1.25),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff'
  },
  selectedHistoryItem: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
  },
  itemMeta: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.75)
  },
  examples: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  cardsOutput: {
    padding: theme.spacing(2),
    minHeight: 560,
    maxHeight: 760,
    overflow: 'auto',
    backgroundColor: '#fff'
  },
  treeCards: {
    display: 'grid',
    gap: theme.spacing(1.5)
  },
  treeCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: '#fff'
  },
  treeCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  cardFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  fieldLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 2
  },
  feedbackPanel: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: '#f8fafc',
    marginBottom: theme.spacing(1.5)
  },
  feedbackRows: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  feedbackRow: {
    display: 'grid',
    gridTemplateColumns: '80px 1fr',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  output: {
    margin: 0,
    padding: theme.spacing(2),
    minHeight: 560,
    maxHeight: 760,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: 13,
    lineHeight: 1.5,
    backgroundColor: '#fff'
  },
  statusRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  field: {
    marginBottom: theme.spacing(1.5)
  },
  hiddenInput: {
    display: 'none'
  },
  fileName: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary
  }
}))

const parseFromEntry = (entry) => entry?.payload?.data?.parse || entry?.data?.parse || {}
const recordingFromEntry = (entry) => entry?.payload?.data?.recording || entry?.data?.recording || {}
const structuredFromEntry = (entry) => parseFromEntry(entry)?.structuredData || {}
const feedbackFromEntry = (entry) => entry?.humanFeedback || entry?.payload?.data?.humanFeedback || entry?.data?.humanFeedback || null
const requestIdFromEntry = (entry) => entry?.requestId || entry?.payload?.requestId || entry?.payload?.data?.requestId || entry?.data?.requestId || null

const statusColor = (status) => {
  if (status === 'parsed') return 'primary'
  if (status === 'needs_review') return 'secondary'
  return 'default'
}

const formatDate = (value) => {
  if (!value) return 'Unknown time'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString()
}

const formatJson = (value) => JSON.stringify(value || null, null, 2)

const getDisplayLabel = (entry) => {
  const recording = recordingFromEntry(entry)
  return entry?.label || recording.originalName || recording.sourceUrl || 'Voice audit run'
}

const getSnippet = (entry) => {
  const parse = parseFromEntry(entry)
  const err = entry?.error?.message || entry?.payload?.error
  const text = parse.transcript || parse.error || err || ''
  return text.length > 140 ? `${text.slice(0, 140)}...` : text
}

const outputForTab = (tab, entry) => {
  const parse = parseFromEntry(entry)
  const recording = recordingFromEntry(entry)
  if (!entry) return 'Select a run from history or run a new test.'
  if (tab === 'Summary') return parse.summary || parse.error || entry?.error?.message || formatJson(entry)
  if (tab === 'Structured JSON') return formatJson(parse.structuredData || parse)
  if (tab === 'Transcript') return parse.transcript || ''
  if (tab === 'Warnings') return formatJson(parse.warnings || [])
  if (tab === 'Files') return formatJson(recording)
  return formatJson(entry)
}

const valueText = (value, fallback = '-') => {
  if (value == null || value === '') return fallback
  if (Array.isArray(value)) return value.length ? value.join(', ') : fallback
  return String(value)
}

const IrrigationVoiceAuditPage = (props) => {
  const classes = useStyles()
  const [history, setHistory] = useState([])
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [workflow, setWorkflow] = useState('timer')

  const [recordingUrl, setRecordingUrl] = useState('')
  const [transcript, setTranscript] = useState('')
  const [file, setFile] = useState(null)
  const [backend, setBackend] = useState('')
  const [model, setModel] = useState('')
  const [siteId, setSiteId] = useState('')
  const [entityType, setEntityType] = useState(WORKFLOWS.timer.defaultEntityType)
  const [entityId, setEntityId] = useState('')
  const [status, setStatus] = useState('')
  const [kind, setKind] = useState('')
  const [needsHumanReview, setNeedsHumanReview] = useState('')
  const [feedbackDraft, setFeedbackDraft] = useState(emptyFeedback())
  const [savingFeedback, setSavingFeedback] = useState(false)

  const selectedParse = parseFromEntry(selected)
  const selectedRecording = recordingFromEntry(selected)
  const selectedRequestId = requestIdFromEntry(selected)
  const workflowConfig = WORKFLOWS[workflow] || WORKFLOWS.timer
  const apiBase = workflowConfig.apiBase

  const historyParams = useMemo(() => {
    const params = new URLSearchParams()
    params.set('limit', '100')
    if (siteId) params.set('siteId', siteId)
    if (entityType) params.set('entityType', entityType)
    if (entityId) params.set('entityId', entityId)
    if (status) params.set('status', status)
    if (kind) params.set('kind', kind)
    if (needsHumanReview) params.set('needsHumanReview', needsHumanReview)
    return params.toString()
  }, [siteId, entityType, entityId, status, kind, needsHumanReview])

  const baseFields = useCallback(() => ({
    backend,
    model,
    siteId,
    entityType,
    entityId
  }), [backend, model, siteId, entityType, entityId])

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true)
    setError('')
    try {
      const response = await axios.get(`${props.url}${apiBase}/history?${historyParams}`)
      const rows = response?.data?.data || []
      setHistory(rows)
      setSelected(current => current || rows[0] || null)
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Failed to load voice audit history.')
    } finally {
      setLoadingHistory(false)
    }
  }, [props.url, apiBase, historyParams])

  useEffect(() => {
    setSelected(null)
    setHistory([])
    loadHistory()
  }, [workflow, loadHistory])

  useEffect(() => {
    const saved = feedbackFromEntry(selected)
    setFeedbackDraft({
      ...emptyFeedback(),
      ...(saved || {}),
      missed: saved?.missed || {},
      wrong: saved?.wrong || {},
      reviewerNote: saved?.reviewerNote || ''
    })
  }, [selected])

  const handleRunUrl = async () => {
    if (!workflowConfig.supportsUrl) {
      setError(`${workflowConfig.label} does not support URL parsing yet. Use file upload or pasted transcript.`)
      return
    }
    if (!recordingUrl.trim()) {
      setError('Paste a recording URL first.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const response = await axios.post(`${props.url}${apiBase}/parse-url`, {
        url: recordingUrl.trim(),
        ...baseFields()
      })
      const entry = {
        requestId: response.data?.requestId || response.data?.data?.requestId || null,
        createdAt: new Date().toISOString(),
        kind: 'url',
        label: recordingUrl.trim(),
        payload: response.data,
        status: response.data?.data?.parse?.needsHumanReview ? 'needs_review' : 'parsed'
      }
      setSelected(entry)
      setActiveTab(0)
      await loadHistory()
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'URL parse failed.')
      await loadHistory()
    } finally {
      setRunning(false)
    }
  }

  const handleRunTranscript = async () => {
    if (!transcript.trim()) {
      setError('Paste transcript text first.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const form = new FormData()
      form.append('transcript', transcript)
      Object.entries(baseFields()).forEach(([key, value]) => {
        if (value) form.append(key, value)
      })
      const response = await axios.post(`${props.url}${apiBase}/parse`, form)
      const entry = {
        requestId: response.data?.requestId || response.data?.data?.requestId || null,
        createdAt: new Date().toISOString(),
        kind: 'transcript',
        label: 'pasted transcript',
        payload: response.data,
        status: response.data?.data?.parse?.needsHumanReview ? 'needs_review' : 'parsed'
      }
      setSelected(entry)
      setActiveTab(0)
      await loadHistory()
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Transcript parse failed.')
      await loadHistory()
    } finally {
      setRunning(false)
    }
  }

  const handleRunFile = async () => {
    if (!file) {
      setError('Choose an audio file first.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      Object.entries(baseFields()).forEach(([key, value]) => {
        if (value) form.append(key, value)
      })
      const response = await axios.post(`${props.url}${apiBase}/parse`, form)
      const entry = {
        requestId: response.data?.requestId || response.data?.data?.requestId || null,
        createdAt: new Date().toISOString(),
        kind: 'file',
        label: file.name,
        payload: response.data,
        status: response.data?.data?.parse?.needsHumanReview ? 'needs_review' : 'parsed'
      }
      setSelected(entry)
      setActiveTab(0)
      await loadHistory()
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'File parse failed.')
      await loadHistory()
    } finally {
      setRunning(false)
    }
  }

  const applyRecordingMetadataToFilters = () => {
    if (selectedRecording.siteId) setSiteId(selectedRecording.siteId)
    if (selectedRecording.entityType) setEntityType(selectedRecording.entityType)
    if (selectedRecording.entityId) setEntityId(selectedRecording.entityId)
  }

  const toggleFeedback = (bucket, key) => {
    setFeedbackDraft(current => ({
      ...current,
      [bucket]: {
        ...(current[bucket] || {}),
        [key]: !current[bucket]?.[key]
      }
    }))
  }

  const handleSaveFeedback = async () => {
    if (workflow !== 'arbor') {
      setError('Feedback saving is currently wired for Arbor voice runs.')
      return
    }
    if (!selectedRequestId) {
      setError('This run does not have a request id yet. Refresh history and select the saved row.')
      return
    }
    setSavingFeedback(true)
    setError('')
    try {
      const response = await axios.post(`${props.url}${apiBase}/${selectedRequestId}/feedback`, feedbackDraft)
      const humanFeedback = response?.data?.data?.humanFeedback || feedbackDraft
      setSelected(current => current
        ? {
            ...current,
            humanFeedback,
            payload: current.payload
              ? {
                  ...current.payload,
                  data: {
                    ...(current.payload.data || {}),
                    humanFeedback
                  }
                }
              : current.payload
          }
        : current)
      setHistory(rows => rows.map(row => {
        const rowId = requestIdFromEntry(row)
        return rowId === selectedRequestId ? { ...row, humanFeedback } : row
      }))
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Feedback save failed.')
    } finally {
      setSavingFeedback(false)
    }
  }

  const renderTreeCards = () => {
    const structuredData = structuredFromEntry(selected)
    const trees = Array.isArray(structuredData.trees) ? structuredData.trees : []
    if (!selected) {
      return (
        <div className={classes.cardsOutput}>
          <Typography variant="body2" color="textSecondary">Select a run from history or run a new Arbor test.</Typography>
        </div>
      )
    }
    if (!trees.length) {
      return (
        <div className={classes.cardsOutput}>
          <Typography variant="body2" color="textSecondary">No tree records were found in this parser output.</Typography>
        </div>
      )
    }
    return (
      <div className={classes.cardsOutput}>
        {workflow === 'arbor' && (
          <div className={classes.feedbackPanel}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2">Parser Feedback</Typography>
                <Typography variant="caption" color="textSecondary">
                  Saved to the audit row for tuning, evals, and future training exports.
                </Typography>
              </Box>
              <Button
                size="small"
                variant={feedbackDraft.accepted ? 'contained' : 'outlined'}
                color={feedbackDraft.accepted ? 'primary' : 'default'}
                onClick={() => setFeedbackDraft(current => ({ ...current, accepted: !current.accepted }))}
              >
                {feedbackDraft.accepted ? 'Accepted' : 'Mark Accepted'}
              </Button>
            </Box>
            <div className={classes.feedbackRows}>
              <div className={classes.feedbackRow}>
                <Typography variant="caption" color="textSecondary">Missed</Typography>
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {FEEDBACK_CATEGORIES.map(([key, label]) => (
                    <Button
                      key={`missed-${key}`}
                      size="small"
                      variant={feedbackDraft.missed?.[key] ? 'contained' : 'outlined'}
                      color={feedbackDraft.missed?.[key] ? 'secondary' : 'default'}
                      onClick={() => toggleFeedback('missed', key)}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              </div>
              <div className={classes.feedbackRow}>
                <Typography variant="caption" color="textSecondary">Wrong</Typography>
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {FEEDBACK_CATEGORIES.map(([key, label]) => (
                    <Button
                      key={`wrong-${key}`}
                      size="small"
                      variant={feedbackDraft.wrong?.[key] ? 'contained' : 'outlined'}
                      color={feedbackDraft.wrong?.[key] ? 'secondary' : 'default'}
                      onClick={() => toggleFeedback('wrong', key)}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              </div>
            </div>
            <TextField
              label="Reviewer note"
              value={feedbackDraft.reviewerNote || ''}
              onChange={(event) => setFeedbackDraft(current => ({ ...current, reviewerNote: event.target.value }))}
              variant="outlined"
              multiline
              rows={2}
              fullWidth
              placeholder="What did the parser miss or get wrong?"
            />
            <Box mt={1} display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSaveFeedback}
                disabled={savingFeedback || !selectedRequestId}
              >
                {savingFeedback ? 'Saving...' : 'Save Feedback'}
              </Button>
              {!selectedRequestId && (
                <Typography variant="caption" color="textSecondary">
                  Refresh history to save feedback for this run.
                </Typography>
              )}
              {feedbackFromEntry(selected)?.updatedAt && (
                <Typography variant="caption" color="textSecondary">
                  Last saved {formatDate(feedbackFromEntry(selected).updatedAt)}
                </Typography>
              )}
            </Box>
          </div>
        )}
        <div className={classes.treeCards}>
          {trees.map((tree, index) => (
            <div key={`${tree.treeNumber || index}-${tree.heardAs || index}`} className={classes.treeCard}>
              <div className={classes.treeCardHeader}>
                <Box>
                  <Typography variant="h6">Tree {tree.treeNumber || index + 1}</Typography>
                  {(tree.groupLabel || tree.groupMemberNumber) && (
                    <Typography variant="caption" color="textSecondary">
                      {valueText(tree.groupLabel)}
                      {tree.groupMemberNumber != null ? ` · group #${tree.groupMemberNumber}` : ''}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {tree.serviceType && <Chip size="small" label={tree.serviceType} color="primary" />}
                  {tree.inferredFromGroup && <Chip size="small" label="inferred group item" />}
                  {Array.isArray(tree.reviewFlags) && tree.reviewFlags.map(flag => (
                    <Chip key={flag} size="small" label={flag} color="secondary" />
                  ))}
                </Box>
              </div>

              <div className={classes.cardFields}>
                <Box><span className={classes.fieldLabel}>Species</span>{valueText(tree.species)}</Box>
                <Box><span className={classes.fieldLabel}>Height</span>{tree.totalHeightFt != null ? `${tree.totalHeightFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Canopy</span>{tree.canopyWidthFt != null ? `${tree.canopyWidthFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Canopy Base</span>{tree.canopyBaseFt != null ? `${tree.canopyBaseFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>DBH</span>{tree.dbhIn != null ? `${tree.dbhIn} in` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Girth</span>{tree.trunkCircumferenceFt != null ? `${tree.trunkCircumferenceFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Density</span>{valueText(tree.canopyDensity)}</Box>
                <Box><span className={classes.fieldLabel}>Drop Zone</span>{valueText([tree.dropZone, tree.dropZoneDirection].filter(Boolean))}</Box>
              </div>

              <Box display="flex" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {valueText(tree.objectives) !== '-' && <Chip size="small" label={`work: ${valueText(tree.objectives)}`} />}
                {valueText(tree.location) !== '-' && <Chip size="small" label={`location: ${valueText(tree.location)}`} />}
                {valueText(tree.utilityLines) !== '-' && <Chip size="small" label={`utilities: ${valueText(tree.utilityLines)}`} />}
                {valueText(tree.obstacles) !== '-' && <Chip size="small" label={`obstacles: ${valueText(tree.obstacles)}`} />}
                {valueText(tree.access) !== '-' && <Chip size="small" label={`access: ${valueText(tree.access)}`} />}
                {valueText(tree.disposal) !== '-' && <Chip size="small" label={`disposal: ${valueText(tree.disposal)}`} />}
                {valueText(tree.equipment) !== '-' && <Chip size="small" label={`equipment: ${valueText(tree.equipment)}`} />}
              </Box>

              {(valueText(tree.futurePlans) !== '-' || valueText(tree.internalNotes) !== '-' || valueText(tree.oneOffReasons) !== '-') && (
                <Box mb={1}>
                  {valueText(tree.futurePlans) !== '-' && <Typography variant="body2"><strong>Future:</strong> {valueText(tree.futurePlans)}</Typography>}
                  {valueText(tree.internalNotes) !== '-' && <Typography variant="body2"><strong>Internal:</strong> {valueText(tree.internalNotes)}</Typography>}
                  {valueText(tree.oneOffReasons) !== '-' && <Typography variant="body2"><strong>One-off:</strong> {valueText(tree.oneOffReasons)}</Typography>}
                </Box>
              )}

              {tree.heardAs && (
                <Typography variant="caption" color="textSecondary">{tree.heardAs}</Typography>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Button component={RouterLink} to="/hmiirrigationdashboard" startIcon={<ArrowBackIcon />} variant="outlined">
            Dashboard
          </Button>
          <Box>
            <Typography variant="h4">Voice Audit</Typography>
            <Typography variant="body2" color="textSecondary">
              Review mobile uploads, rerun recordings, and inspect parser output across voice workflows.
            </Typography>
          </Box>
        </div>
        <Box display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={loadHistory}
            disabled={loadingHistory}
          >
            Refresh
          </Button>
          <Button
            startIcon={<HistoryIcon />}
            variant="outlined"
            href={`${props.url}/api/mobileapp/irrigation/timer-voice/harness`}
            target="_blank"
          >
            Raw Harness
          </Button>
        </Box>
      </div>

      {error && (
        <Box mb={2}>
          <Chip label={error} color="secondary" />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.panel}>
            <Typography className={classes.sectionTitle}>Run Test</Typography>
            <Box className={classes.field}>
              <InputLabel>Workflow</InputLabel>
              <Select
                value={workflow}
                onChange={(event) => {
                  const nextWorkflow = event.target.value
                  const nextConfig = WORKFLOWS[nextWorkflow] || WORKFLOWS.timer
                  const previousDefaults = Object.values(WORKFLOWS).map(item => item.defaultEntityType)
                  setWorkflow(nextWorkflow)
                  setError('')
                  setSelected(null)
                  if (!entityType || previousDefaults.includes(entityType)) {
                    setEntityType(nextConfig.defaultEntityType)
                  }
                  if (!nextConfig.supportsUrl && kind === 'url') {
                    setKind('')
                  }
                }}
                fullWidth
              >
                {Object.entries(WORKFLOWS).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.label}</MenuItem>
                ))}
              </Select>
            </Box>
            <TextField
              className={classes.field}
              label="Recording URL"
              value={recordingUrl}
              onChange={(event) => setRecordingUrl(event.target.value)}
              multiline
              rows={3}
              variant="outlined"
              fullWidth
              placeholder="https://dflspaces.sfo2.digitaloceanspaces.com/..."
              disabled={!workflowConfig.supportsUrl}
              helperText={workflowConfig.supportsUrl ? '' : 'URL parsing is not enabled for this workflow yet.'}
            />
            <input
              id="voice-audit-file"
              className={classes.hiddenInput}
              type="file"
              accept=".m4a,.mp3,.wav,audio/*"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
            <label htmlFor="voice-audit-file">
              <Button component="span" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                Choose Audio File
              </Button>
            </label>
            {file && (
              <Typography variant="caption" className={classes.fileName} display="block">
                {file.name}
              </Typography>
            )}
            <TextField
              className={classes.field}
              label="Transcript"
              value={transcript}
              onChange={(event) => setTranscript(event.target.value)}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              style={{ marginTop: 12 }}
              placeholder={workflowConfig.transcriptPlaceholder}
            />
            {Array.isArray(workflowConfig.examples) && workflowConfig.examples.length > 0 && (
              <div className={classes.examples}>
                {workflowConfig.examples.map(example => (
                  <Button
                    key={example.label}
                    size="small"
                    variant="outlined"
                    onClick={() => setTranscript(example.transcript)}
                  >
                    {example.label}
                  </Button>
                ))}
              </div>
            )}

            <div className={classes.controlsGrid}>
              <TextField label="Backend" value={backend} onChange={(event) => setBackend(event.target.value)} variant="outlined" placeholder="server default" />
              <TextField label="Model" value={model} onChange={(event) => setModel(event.target.value)} variant="outlined" placeholder="base.en" />
              <TextField label="Site ID" value={siteId} onChange={(event) => setSiteId(event.target.value)} variant="outlined" />
              <TextField label="Entity Type" value={entityType} onChange={(event) => setEntityType(event.target.value)} variant="outlined" placeholder={workflowConfig.defaultEntityType} />
              <TextField label="Entity ID" value={entityId} onChange={(event) => setEntityId(event.target.value)} variant="outlined" />
            </div>

            <div className={classes.actions}>
              <Button variant="contained" color="primary" startIcon={<LinkIcon />} onClick={handleRunUrl} disabled={running || !workflowConfig.supportsUrl}>
                Parse URL
              </Button>
              <Button variant="contained" color="primary" startIcon={<CloudUploadIcon />} onClick={handleRunFile} disabled={running}>
                Parse File
              </Button>
              <Button variant="outlined" color="primary" startIcon={<DescriptionIcon />} onClick={handleRunTranscript} disabled={running}>
                Parse Transcript
              </Button>
            </div>
          </Paper>

          <Box mt={2}>
            <Paper className={classes.panel}>
              <Typography className={classes.sectionTitle}>History Filters</Typography>
              <div className={classes.controlsGrid}>
                <Box>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} onChange={(event) => setStatus(event.target.value)} fullWidth>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="parsed">Parsed</MenuItem>
                    <MenuItem value="needs_review">Needs review</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </Box>
                <Box>
                  <InputLabel>Kind</InputLabel>
                  <Select value={kind} onChange={(event) => setKind(event.target.value)} fullWidth>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="file">File</MenuItem>
                    <MenuItem value="url">URL</MenuItem>
                    <MenuItem value="transcript">Transcript</MenuItem>
                  </Select>
                </Box>
                <Box>
                  <InputLabel>Review</InputLabel>
                  <Select value={needsHumanReview} onChange={(event) => setNeedsHumanReview(event.target.value)} fullWidth>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">Needs review</MenuItem>
                    <MenuItem value="false">No review</MenuItem>
                  </Select>
                </Box>
              </div>
            </Paper>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.panel}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography className={classes.sectionTitle}>Permanent History</Typography>
              {loadingHistory && <CircularProgress size={20} />}
            </Box>
            <div className={classes.historyList}>
              {history.length === 0 && !loadingHistory ? (
                <Typography variant="body2" color="textSecondary">No audit rows found.</Typography>
              ) : history.map((entry) => (
                <Button
                  key={entry.requestId || `${entry.createdAt}-${entry.label}`}
                  className={`${classes.historyItem} ${selected?.requestId === entry.requestId ? classes.selectedHistoryItem : ''}`}
                  onClick={() => {
                    setSelected(entry)
                    setActiveTab(0)
                  }}
                >
                  <Box width="100%">
                    <Box display="flex" justifyContent="space-between" style={{ gap: 8 }}>
                      <Typography variant="subtitle2">{getDisplayLabel(entry)}</Typography>
                      <Chip size="small" label={entry.status || 'unknown'} color={statusColor(entry.status)} />
                    </Box>
                    <Typography variant="caption" color="textSecondary">{formatDate(entry.createdAt)}</Typography>
                    <Typography variant="body2" color="textSecondary">{getSnippet(entry)}</Typography>
                    <div className={classes.itemMeta}>
                      {entry.kind && <Chip size="small" label={entry.kind} />}
                      {entry.siteId && <Chip size="small" label={`site ${entry.siteId}`} />}
                      {entry.confidence != null && <Chip size="small" label={`confidence ${Number(entry.confidence).toFixed(2)}`} />}
                      {entry.processingMs != null && <Chip size="small" label={`${entry.processingMs}ms`} />}
                    </div>
                  </Box>
                </Button>
              ))}
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper>
            <div className={classes.statusRow}>
              <Chip label={selected?.status || 'No run selected'} color={statusColor(selected?.status)} />
              {selectedParse.confidence != null && <Chip label={`Confidence ${Number(selectedParse.confidence).toFixed(2)}`} />}
              {selectedParse.needsHumanReview && <Chip label="Needs review" color="secondary" />}
              {selectedRecording.sourceUrl && (
                <Chip
                  label="Source URL"
                  component={Link}
                  clickable
                  href={selectedRecording.sourceUrl}
                  target="_blank"
                />
              )}
              {(selectedRecording.siteId || selectedRecording.entityId) && (
                <Button size="small" onClick={applyRecordingMetadataToFilters}>Use Metadata Filters</Button>
              )}
            </div>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              className={classes.tabs}
            >
              {TABS.map(tab => <Tab key={tab} label={tab} />)}
            </Tabs>
            {TABS[activeTab] === 'Tree Cards'
              ? renderTreeCards()
              : <pre className={classes.output}>{outputForTab(TABS[activeTab], selected)}</pre>}
          </Paper>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Divider />
        <Typography variant="caption" color="textSecondary">
          Audit rows are stored in Mongo collection irrigation_voice_audits. The raw server harness is still available for quick isolated checks.
        </Typography>
      </Box>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(IrrigationVoiceAuditPage)
